import { apiCaller } from '@/api/apiCaller';
import { apihost } from '@/api/urls';
import { AxiosPromise } from 'axios';
import {
  ClassificationFamilyFilter,
  FamilyFilter,
  FamilyGroupFilter,
  FamilyVersionFilter,
} from '@/modules/neovehicle/models/filters';
import {
  Family,
  FamilySegment,
  FamilySegments, Tag,
} from '@/modules/neovehicle/models';
import { CountryEnum } from '@/models/Enums';
import { buildDownloadFile } from '@/modules/neovehicle/services/services';
import {
  FamiliesToFamilyGroupEdit,
  FamilyEdit, FamilyGroupEdit,
  FamilyGroupManagement,
  FamilyManagement,
  FamilyVersionEdit,
  FamilyVersionFamiliesEdit,
  FamilyVersionManagement,
  FamilyVersionModelsEdit,
  LastClassifications,
  FamilyGroupCreateModel, FamilyCreateModel,
} from '@/modules/neovehicle/models/family';
import { SocialClass } from '../models/socialClass';

const getFamiliesSegment = (
  filter: ClassificationFamilyFilter,
): AxiosPromise<FamilySegment[]> =>
  apiCaller.get(
    `/neovehicle/classification/families?isClassified=${filter.isClassified}&country=${filter.country}&name=${filter.name}`,
    apihost,
  );

const getFamiliesBySegment = (filter: {
  idBrand: number;
  idSegment: number;
  country: CountryEnum;
}): AxiosPromise<Family[]> =>
  apiCaller.get(
    `/neovehicle/segments/families?idbrand=${filter.idBrand}&idsegment=${filter.idSegment}&country=${filter.country}`,
    apihost,
  );

const exportFamiliesSegment = (country: CountryEnum): Promise<any> =>
  apiCaller
    .getFile(`/neovehicle/families/export?country=${country}`, apihost)
    .then(
      buildDownloadFile(
        `relatorio_familias_e_segmentos_${new Date().toISOString()}.xlsx`,
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      ),
    );

const saveFamiliesSegment = (family: {
  idFamily: number;
  country: number;
  segments: Array<{ id: number; idBrand: number }>;
}): AxiosPromise<void> =>
  apiCaller.post(
    `/neovehicle/classification/families/${family.idFamily}`,
    apihost,
    family,
  );

const saveFamilySegmentFromOption = (family: {
  idFamily: number;
  country: number;
  familySegmentOption: number;
  idVehicleType: number;
  idBrands: number[];
}): AxiosPromise<void> =>
  apiCaller.post(
    `/neovehicle/classification/family/option`,
    apihost,
    family,
  );

const getFamilySegments = (filter: {
  idFamily: number;
  idVehicleType: number;
  country: CountryEnum;
}): AxiosPromise<FamilySegments[]> =>
  apiCaller.get(
    `/neovehicle/brands/families/segments?idFamily=${filter.idFamily}&country=${filter.country}&idVehicleType=${filter.idVehicleType}`,
    apihost,
  );

const cloneFamilySegment = (payload: {
  idFamily: number;
  country: number;
  idFamilyClone: number;
  idVehicleType: number;
}): AxiosPromise<void> =>
  apiCaller.post(
    `/neovehicle/classification/family/clone`,
    apihost,
    payload,
  );

const getFamiliesWithSegment = (filter: {
  familyName: string;
  idVehicleType: number;
  country: CountryEnum;
}): AxiosPromise<Family[]> =>
  apiCaller.get(
    `/neovehicle/families/segments?familyName=${filter.familyName}&idVehicleType=${filter.idVehicleType}&country=${filter.country}`,
    apihost,
  );

const getTagsByContext = (): AxiosPromise<Tag[]> =>
  apiCaller.get(`/neovehicle/families/tags`, apihost);

const getTagsByFamilyVersion = (filter: {
  idFamily: number;
  idFamilyVersion: number;
}): AxiosPromise<Tag[]> =>
  apiCaller.get(`/neovehicle/family/tags?idFamilyVersion=${filter.idFamilyVersion}&idFamily=${filter.idFamily}`, apihost);

const getFamilyVersions = (filter: FamilyVersionFilter): AxiosPromise<any[]> =>
  apiCaller.getParams(`/neovehicle/families/versions`, apihost, filter);

const getFamilies = (filter: FamilyFilter): AxiosPromise<any[]> =>
  apiCaller.getParams(`/neovehicle/families`, apihost, filter);

const getFamilyGroups = (filter: FamilyGroupFilter): AxiosPromise<FamilyGroupManagement[]> =>
  apiCaller.getParams(`/neovehicle/familygroups/families/`, apihost, filter);

const deleteFamilyVersion = (familyVersion: FamilyVersionManagement): AxiosPromise<void> =>
  apiCaller.del(`/neovehicle/families/${familyVersion.idFamily}/versions/${familyVersion.idFamilyVersion}`, apihost);

const deleteFamily = (family: FamilyManagement): AxiosPromise<void> =>
  apiCaller.del(`/neovehicle/familygroups/${family.idFamilyGroup}/families/${family.idFamily}`, apihost);

const deleteFamilyGroup = (familyGroup: FamilyGroupManagement): AxiosPromise<void> =>
    apiCaller.del(`/neovehicle/familygroups/${familyGroup.idFamilyGroup}`, apihost);

const updateFamilyVersion = (familyVersion: FamilyVersionEdit): AxiosPromise<void> =>
  apiCaller.put(
    `/neovehicle/families/${familyVersion.idFamily}/versions/${familyVersion.idFamilyVersion}`,
    apihost,
    familyVersion,
  );

const updateFamily = (family: FamilyEdit): AxiosPromise<void> =>
  apiCaller.put(
    `/neovehicle/familygroups/${family.idFamilyGroup}/families/${family.idFamily}`,
    apihost,
    family,
  );

const updateFamilyGroup = (familyGroup: FamilyGroupEdit): AxiosPromise<void> =>
  apiCaller.put(
    `/neovehicle/familygroups/${familyGroup.idFamilyGroup}`,
    apihost,
    familyGroup,
  );

const updateFamilyVersionModels = (edit: FamilyVersionModelsEdit): AxiosPromise<void> =>
apiCaller.put(
  `/neovehicle/familyversions/models`,
  apihost,
  edit,
);

const getLastClassifications = (country: CountryEnum): AxiosPromise<LastClassifications> =>
  apiCaller.get(`/neovehicle/families/lastclassfications?country=${country}`, apihost);

const updateFamilyFromVersions = (edit: FamilyVersionFamiliesEdit): AxiosPromise<void> =>
  apiCaller.put(
    `/neovehicle/familyversions/families`,
    apihost,
    edit,
  );

const updateFamilyGroupFromFamilies = (edit: FamiliesToFamilyGroupEdit): AxiosPromise<void> =>
  apiCaller.put(
    `/neovehicle/families/groups`,
    apihost,
    edit,
  );

const insertFamilyGroup = (family: FamilyGroupCreateModel): AxiosPromise<void> =>
  apiCaller.post(
    `/neovehicle/families/groups`,
    apihost,
    family,
  );

const insertFamily = (family: FamilyCreateModel): AxiosPromise<void> =>
  apiCaller.post(
    `/neovehicle/families`,
    apihost,
    family,
  );

const getSocialClasses = (filter: {
    country: CountryEnum;
  }): AxiosPromise<SocialClass[]> =>
    apiCaller.getParams(`/neovehicle/socialclasses`, apihost, filter);

export const familyService = {
  getFamiliesSegment,
  exportFamiliesSegment,
  getFamiliesBySegment,
  saveFamiliesSegment,
  getFamilySegments,
  saveFamilySegmentFromOption,
  cloneFamilySegment,
  getFamiliesWithSegment,
  getTagsByContext,
  getTagsByFamilyVersion,

  getFamilyVersions,
  getFamilies,
  getFamilyGroups,
  deleteFamilyVersion,
  deleteFamily,
  deleteFamilyGroup,
  updateFamilyVersion,
  updateFamily,
  updateFamilyGroup,
  updateFamilyVersionModels,

  getLastClassifications,
  updateFamilyFromVersions,
  updateFamilyGroupFromFamilies,

  insertFamilyGroup,
  insertFamily,

  getSocialClasses,
};
